import { API_BASE } from "../config.js";

// El canal web responde en ≤30 s (RESPONSE_TIMEOUT_MS del backend); damos un
// margen de 2 s antes de marcar el mensaje como fallido.
const DEFAULT_TIMEOUT_MS = 32_000;

let sessionToken = null;

export function setSessionToken(token) {
  sessionToken = token || null;
}

export class ApiError extends Error {
  constructor(status, code, message) {
    super(message || `HTTP ${status}`);
    this.status = status;
    this.code = code || null;
  }
}

/**
 * ¿El error dice que la credencial de sesión ya no sirve?
 *
 * Tres formas de lo mismo, todas resueltas por un /start nuevo (no por
 * reintentar con el mismo token):
 *  - 401: token vencido (TTL deslizante de 30 min) o ausente.
 *  - 403 session_conversation_mismatch: el token es de otra conversación.
 *  - 404 CONVERSATION_NOT_FOUND: la conversación expiró del lado del server
 *    (mismo timeout de 30 min de inactividad).
 *
 * Los otros 4xx con code propio quedan afuera a propósito (invalid_api_key,
 * origin_not_allowed, UNKNOWN_ACTION): renovar no los arregla y el reintento
 * sería un loop.
 */
export function isSessionError(err) {
  if (!(err instanceof ApiError)) return false;
  if (err.status === 401) return true;
  if (err.status === 403) return err.code === "session_conversation_mismatch";
  return err.status === 404 && err.code === "CONVERSATION_NOT_FOUND";
}

/**
 * Descarga binaria autenticada. No usa apiFetch porque la respuesta es un blob,
 * y no se puede resolver con <a href> ni window.open: esos no mandan el header
 * Authorization y el server rechaza el token por query (queda en logs).
 * @returns {Promise<Blob>}
 */
export async function apiFetchBlob(path, { timeoutMs = DEFAULT_TIMEOUT_MS } = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  const headers = {};
  if (sessionToken) headers.Authorization = `Bearer ${sessionToken}`;

  try {
    const res = await fetch(`${API_BASE}${path}`, { headers, signal: controller.signal });
    if (!res.ok) {
      let payload = null;
      try { payload = await res.json(); } catch { /* respuesta sin body JSON */ }
      throw new ApiError(res.status, payload?.code, payload?.error);
    }
    return await res.blob();
  } catch (err) {
    if (err instanceof ApiError) throw err;
    if (err?.name === "AbortError") throw new ApiError(0, "timeout", "La descarga tardó demasiado");
    throw new ApiError(0, "network", "Sin conexión con el servidor");
  } finally {
    clearTimeout(timer);
  }
}

export async function apiFetch(path, { method = "GET", body, timeoutMs = DEFAULT_TIMEOUT_MS } = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  const headers = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (sessionToken) headers.Authorization = `Bearer ${sessionToken}`;

  try {
    const res = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: controller.signal
    });

    let payload = null;
    try { payload = await res.json(); } catch { /* respuesta sin body */ }

    if (!res.ok) {
      throw new ApiError(res.status, payload?.code, payload?.error);
    }
    return payload;
  } catch (err) {
    if (err instanceof ApiError) throw err;
    if (err?.name === "AbortError") throw new ApiError(0, "timeout", "La respuesta tardó demasiado");
    throw new ApiError(0, "network", "Sin conexión con el servidor");
  } finally {
    clearTimeout(timer);
  }
}
